/**
 * Neural Mesh Navigator Web Component
 * Built with Lit for universal framework compatibility
 */

import { LitElement, html, css } from 'lit'
import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import gsap from 'gsap'

import { NeuralNetwork } from '../core/neural-network.js'
import { SynapsePulseSystem } from '../core/synapse-pulse.js'
import { CameraController } from '../core/camera-controller.js'
import { NeuralSearchEngine } from '../utils/search-engine.js'
import { defaultNavigationData } from '../data/navigation-data.js'

export class NeuralMeshNavigator extends LitElement {
  static properties = {
    theme: { type: String },
    pulseColor: { type: String, attribute: 'pulse-color' },
    neuronColor: { type: String, attribute: 'neuron-color' },
    synapseColor: { type: String, attribute: 'synapse-color' },
    navigationData: { type: Object, attribute: 'navigation-data' },
    showSearch: { type: Boolean, state: true },
    searchQuery: { type: String, state: true },
    searchResults: { type: Array, state: true },
    activeNode: { type: Object, state: true },
    isLoading: { type: Boolean, state: true }
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }

    .canvas-container {
      position: absolute;
      inset: 0;
    }

    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }

    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 51, 71, 0.95);
      z-index: 100;
      transition: opacity 0.5s ease;
    }

    .loading-overlay.hidden {
      opacity: 0;
      pointer-events: none;
    }

    .loader {
      width: 80px;
      height: 80px;
      border: 3px solid transparent;
      border-top-color: #00d4ff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .search-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 15vh;
      background: rgba(0, 51, 71, 0.85);
      backdrop-filter: blur(20px);
      z-index: 50;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    .search-overlay.visible {
      opacity: 1;
      pointer-events: auto;
    }

    .search-container {
      width: 90%;
      max-width: 600px;
      transform: translateY(-20px);
      transition: transform 0.3s ease;
    }

    .search-overlay.visible .search-container {
      transform: translateY(0);
    }

    .search-input-wrapper {
      position: relative;
    }

    .search-icon {
      position: absolute;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      color: #00d4ff;
    }

    .search-input {
      width: 100%;
      padding: 20px 20px 20px 60px;
      font-size: 18px;
      font-family: 'Exo 2', sans-serif;
      color: white;
      background: rgba(0, 212, 255, 0.1);
      border: 2px solid rgba(0, 212, 255, 0.3);
      border-radius: 16px;
      outline: none;
      transition: all 0.3s ease;
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .search-input:focus {
      background: rgba(0, 212, 255, 0.15);
      border-color: rgba(0, 212, 255, 0.6);
      box-shadow: 0 0 40px rgba(0, 212, 255, 0.2);
    }

    .search-results {
      margin-top: 16px;
      background: rgba(0, 51, 71, 0.9);
      border: 1px solid rgba(0, 212, 255, 0.2);
      border-radius: 12px;
      overflow: hidden;
      max-height: 400px;
      overflow-y: auto;
    }

    .search-result-item {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-bottom: 1px solid rgba(0, 212, 255, 0.1);
    }

    .search-result-item:last-child {
      border-bottom: none;
    }

    .search-result-item:hover {
      background: rgba(0, 212, 255, 0.15);
      transform: translateX(8px);
    }

    .result-icon {
      font-size: 24px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 212, 255, 0.2);
      border-radius: 10px;
    }

    .result-content {
      flex: 1;
    }

    .result-label {
      font-family: 'Orbitron', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: white;
      margin-bottom: 4px;
    }

    .result-description {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
    }

    .result-category {
      font-size: 10px;
      padding: 4px 8px;
      background: rgba(0, 212, 255, 0.2);
      border-radius: 4px;
      color: #00d4ff;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .tooltip {
      position: absolute;
      pointer-events: none;
      background: rgba(0, 51, 71, 0.95);
      border: 1px solid rgba(0, 212, 255, 0.4);
      border-radius: 12px;
      padding: 12px 16px;
      z-index: 30;
      opacity: 0;
      transform: translate(-50%, -100%) translateY(-15px);
      transition: opacity 0.2s ease;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .tooltip.visible {
      opacity: 1;
    }

    .tooltip-icon {
      font-size: 20px;
      margin-right: 8px;
    }

    .tooltip-label {
      font-family: 'Orbitron', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .tooltip-description {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
      margin-top: 4px;
    }

    /* ===== MINIMAP STYLES ===== */
    .minimap {
      position: absolute;
      bottom: 80px;
      right: 20px;
      width: 160px;
      height: 160px;
      background: rgba(0, 26, 37, 0.9);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 12px;
      overflow: hidden;
      z-index: 20;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .minimap-header {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      padding: 8px 10px;
      background: linear-gradient(180deg, rgba(0, 51, 71, 0.9) 0%, transparent 100%);
      z-index: 2;
    }

    .minimap-label {
      font-family: 'Orbitron', sans-serif;
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: rgba(0, 212, 255, 0.7);
    }

    .minimap-canvas {
      width: 100%;
      height: 100%;
    }

    .minimap-viewport {
      position: absolute;
      border: 1px solid rgba(255, 107, 157, 0.6);
      background: rgba(255, 107, 157, 0.1);
      border-radius: 2px;
      pointer-events: none;
      transition: all 0.1s ease;
    }

    .minimap-node-count {
      position: absolute;
      bottom: 6px;
      right: 8px;
      font-family: 'Exo 2', sans-serif;
      font-size: 9px;
      color: rgba(0, 212, 255, 0.5);
    }

    .active-node-panel {
      position: absolute;
      top: 20px;
      left: 20px;
      background: rgba(0, 51, 71, 0.9);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 16px;
      padding: 20px;
      min-width: 280px;
      z-index: 20;
      backdrop-filter: blur(10px);
      transform: translateX(-110%);
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .active-node-panel.visible {
      transform: translateX(0);
    }

    .panel-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
    }

    .panel-icon {
      font-size: 32px;
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 212, 255, 0.2);
      border-radius: 12px;
    }

    .panel-title {
      font-family: 'Orbitron', sans-serif;
      font-size: 18px;
      font-weight: 700;
      color: white;
    }

    .panel-category {
      font-size: 10px;
      color: #00d4ff;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .panel-description {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.6;
      margin-bottom: 16px;
    }

    .panel-connections {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .connection-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: rgba(0, 212, 255, 0.1);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 20px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .connection-chip:hover {
      background: rgba(0, 212, 255, 0.2);
      border-color: rgba(0, 212, 255, 0.5);
    }

    .navigate-btn {
      display: block;
      width: 100%;
      margin-top: 16px;
      padding: 12px;
      font-family: 'Orbitron', sans-serif;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: white;
      background: linear-gradient(135deg, #00d4ff, #0093bf);
      border: none;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .navigate-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
    }

    .close-panel-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .close-panel-btn:hover {
      background: rgba(255, 107, 157, 0.3);
    }

    @media (max-width: 768px) {
      .minimap {
        width: 120px;
        height: 120px;
        bottom: 60px;
        right: 10px;
      }

      .active-node-panel {
        left: 10px;
        right: 10px;
        min-width: auto;
      }

      .search-input {
        font-size: 16px;
        padding: 16px 16px 16px 50px;
      }
    }
  `

  constructor() {
    super()
    this.theme = 'cosmic'
    this.pulseColor = '#ff6b9d'
    this.neuronColor = '#00d4ff'
    this.synapseColor = '#4a5568'
    this.navigationData = null
    this.showSearch = false
    this.searchQuery = ''
    this.searchResults = []
    this.activeNode = null
    this.isLoading = true

    this.scene = null
    this.camera = null
    this.renderer = null
    this.composer = null
    this.neuralNetwork = null
    this.pulseSystem = null
    this.cameraController = null
    this.searchEngine = null
    this.clock = new THREE.Clock()

    // Minimap
    this.minimapCanvas = null
    this.minimapCtx = null
    this.minimapData = null

    this.tooltipData = null
    this.tooltipPosition = { x: 0, y: 0 }

    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('resize', this.handleResize)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('resize', this.handleResize)
    this.dispose()
  }

  firstUpdated() {
    this.initThreeJS()
    this.initMinimap()
  }

  async initThreeJS() {
    const container = this.shadowRoot.querySelector('.canvas-container')
    const width = container.clientWidth
    const height = container.clientHeight

    // Scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x001a25)
    this.scene.fog = new THREE.FogExp2(0x001a25, 0.03)

    // Camera
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
    this.camera.position.set(0, 3, 12)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    })
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.2
    container.appendChild(this.renderer.domElement)

    // Post-processing
    this.composer = new EffectComposer(this.renderer)
    this.composer.addPass(new RenderPass(this.scene, this.camera))
    
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      1.5,
      0.4,
      0.85
    )
    this.composer.addPass(bloomPass)

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404060, 0.5)
    this.scene.add(ambientLight)

    const mainLight = new THREE.DirectionalLight(0xffffff, 0.8)
    mainLight.position.set(10, 20, 10)
    this.scene.add(mainLight)

    const fillLight = new THREE.DirectionalLight(0x00d4ff, 0.3)
    fillLight.position.set(-10, -10, -10)
    this.scene.add(fillLight)

    // Initialize systems
    const data = this.navigationData || defaultNavigationData
    
    this.neuralNetwork = new NeuralNetwork(this.scene, data, {
      neuronBaseSize: 0.35
    })

    this.pulseSystem = new SynapsePulseSystem(this.scene, {
      pulseColor: parseInt(this.pulseColor.replace('#', '0x'))
    })

    this.cameraController = new CameraController(
      this.camera,
      this.renderer.domElement
    )

    this.searchEngine = new NeuralSearchEngine(data.nodes)
    this.searchEngine.loadHistory()

    // Store data for minimap
    this.minimapData = data

    // Event listeners for canvas
    this.renderer.domElement.addEventListener('mousemove', this.handleMouseMove)
    this.renderer.domElement.addEventListener('click', this.handleClick)

    // Start animation
    this.isLoading = false
    this.animate()
  }

  initMinimap() {
    this.minimapCanvas = this.shadowRoot.querySelector('.minimap-canvas')
    if (!this.minimapCanvas) return

    this.minimapCtx = this.minimapCanvas.getContext('2d')
    
    // Set canvas resolution
    const dpr = window.devicePixelRatio || 1
    const rect = this.minimapCanvas.getBoundingClientRect()
    this.minimapCanvas.width = rect.width * dpr
    this.minimapCanvas.height = rect.height * dpr
    this.minimapCtx.scale(dpr, dpr)
  }

  renderMinimap() {
    if (!this.minimapCtx || !this.minimapData) return

    const ctx = this.minimapCtx
    const canvas = this.minimapCanvas
    const rect = canvas.getBoundingClientRect()
    const width = rect.width
    const height = rect.height

    // Clear
    ctx.clearRect(0, 0, width, height)

    // Calculate bounds
    const nodes = this.minimapData.nodes
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity

    nodes.forEach(node => {
      minX = Math.min(minX, node.position.x)
      maxX = Math.max(maxX, node.position.x)
      minY = Math.min(minY, node.position.y)
      maxY = Math.max(maxY, node.position.y)
    })

    const padding = 25
    const scaleX = (width - padding * 2) / (maxX - minX || 1)
    const scaleY = (height - padding * 2) / (maxY - minY || 1)
    const scale = Math.min(scaleX, scaleY)

    const offsetX = width / 2
    const offsetY = height / 2

    // Draw synapses (connections)
    ctx.strokeStyle = 'rgba(0, 212, 255, 0.2)'
    ctx.lineWidth = 1

    nodes.forEach(node => {
      if (!node.connections) return
      
      const x1 = offsetX + node.position.x * scale
      const y1 = offsetY - node.position.y * scale

      node.connections.forEach(targetId => {
        const target = nodes.find(n => n.id === targetId)
        if (!target) return

        const x2 = offsetX + target.position.x * scale
        const y2 = offsetY - target.position.y * scale

        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()
      })
    })

    // Draw nodes
    const categories = this.minimapData.categories

    nodes.forEach(node => {
      const x = offsetX + node.position.x * scale
      const y = offsetY - node.position.y * scale
      const category = categories[node.category] || categories.core
      const radius = 3 + node.importance * 3

      // Glow effect
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius * 2)
      gradient.addColorStop(0, category.color)
      gradient.addColorStop(1, 'transparent')

      ctx.beginPath()
      ctx.arc(x, y, radius * 2, 0, Math.PI * 2)
      ctx.fillStyle = gradient
      ctx.fill()

      // Core
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fillStyle = category.color
      ctx.fill()

      // Highlight active node
      if (this.activeNode && this.activeNode.id === node.id) {
        ctx.beginPath()
        ctx.arc(x, y, radius + 4, 0, Math.PI * 2)
        ctx.strokeStyle = '#ff6b9d'
        ctx.lineWidth = 2
        ctx.stroke()
      }
    })

    // Draw camera viewport indicator
    this.drawMinimapViewport(ctx, width, height, scale, offsetX, offsetY)
  }

  drawMinimapViewport(ctx, width, height, scale, offsetX, offsetY) {
    if (!this.camera || !this.cameraController) return

    const target = this.cameraController.target
    const camX = offsetX + target.x * scale
    const camY = offsetY - target.y * scale

    // Viewport rectangle
    const viewSize = 20
    
    ctx.strokeStyle = 'rgba(255, 107, 157, 0.6)'
    ctx.lineWidth = 1.5
    ctx.setLineDash([3, 3])
    
    ctx.strokeRect(
      camX - viewSize / 2,
      camY - viewSize / 2,
      viewSize,
      viewSize
    )
    
    ctx.setLineDash([])

    // Center dot
    ctx.beginPath()
    ctx.arc(camX, camY, 3, 0, Math.PI * 2)
    ctx.fillStyle = '#ff6b9d'
    ctx.fill()
  }

  animate() {
    if (!this.renderer) return

    requestAnimationFrame(() => this.animate())

    const deltaTime = this.clock.getDelta()

    // Update systems
    if (this.neuralNetwork) {
      this.neuralNetwork.update(deltaTime)
    }

    if (this.pulseSystem) {
      this.pulseSystem.update(deltaTime)
    }

    if (this.cameraController) {
      this.cameraController.update()
    }

    // Render minimap (throttled)
    if (Math.random() < 0.1) { // ~6 fps for minimap
      this.renderMinimap()
    }

    // Render main scene
    this.composer.render()
  }

  handleKeyDown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        setTimeout(() => {
          const input = this.shadowRoot.querySelector('.search-input')
          if (input) input.focus()
        }, 100)
      }
    }

    if (event.key === 'Escape') {
      if (this.showSearch) {
        this.showSearch = false
      } else if (this.activeNode) {
        this.closeNodePanel()
      }
    }
  }

  handleMouseMove(event) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const node = this.neuralNetwork.getNodeAtPosition(x, y, this.camera)

    if (node) {
      this.tooltipData = node
      this.tooltipPosition = { x: event.clientX, y: event.clientY }
      this.neuralNetwork.highlightNeuron(node.id)
      this.renderer.domElement.style.cursor = 'pointer'
    } else {
      if (this.tooltipData) {
        this.neuralNetwork.unhighlightNeuron(this.tooltipData.id)
      }
      this.tooltipData = null
      this.renderer.domElement.style.cursor = 'grab'
    }

    this.requestUpdate()
  }

  handleClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const node = this.neuralNetwork.getNodeAtPosition(x, y, this.camera)

    if (node) {
      this.selectNode(node)
    }
  }

  selectNode(node) {
    this.activeNode = node
    this.neuralNetwork.selectNeuron(node.id)
    this.cameraController.focusOnNode(node)
    this.searchEngine.addToHistory(node.id)

    // Send pulse to connected nodes
    if (node.connections) {
      node.connections.forEach((targetId, index) => {
        const targetNode = this.neuralNetwork.getNodeById(targetId)
        if (targetNode) {
          setTimeout(() => {
            this.pulseSystem.sendPulse(node, targetNode)
          }, index * 150)
        }
      })
    }

    this.dispatchEvent(new CustomEvent('node-selected', {
      detail: { node },
      bubbles: true,
      composed: true
    }))

    // Force minimap update
    this.renderMinimap()
  }

  navigateToNode(node) {
    if (node.url) {
      this.dispatchEvent(new CustomEvent('navigate', {
        detail: { url: node.url, node },
        bubbles: true,
        composed: true
      }))
      
      if (node.url.startsWith('#')) {
        const element = document.querySelector(node.url)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
  }

  closeNodePanel() {
    this.activeNode = null
    this.cameraController.resetView()
    this.renderMinimap()
  }

  handleSearchInput(event) {
    this.searchQuery = event.target.value
    this.searchResults = this.searchEngine.search(this.searchQuery)
  }

  selectSearchResult(node) {
    this.showSearch = false
    this.searchQuery = ''
    this.selectNode(node)
  }

  handleResize() {
    if (!this.renderer) return

    const container = this.shadowRoot.querySelector('.canvas-container')
    const width = container.clientWidth
    const height = container.clientHeight

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
    this.composer.setSize(width, height)

    // Reinit minimap
    this.initMinimap()
  }

  dispose() {
    if (this.neuralNetwork) this.neuralNetwork.dispose()
    if (this.pulseSystem) this.pulseSystem.dispose()
    if (this.cameraController) this.cameraController.dispose()
    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.domElement.remove()
    }
  }

  render() {
    const data = this.navigationData || defaultNavigationData
    const nodeCount = data?.nodes?.length || 0

    return html`
      <div class="canvas-container"></div>

      <!-- Loading Overlay -->
      <div class="loading-overlay ${this.isLoading ? '' : 'hidden'}">
        <div class="loader"></div>
      </div>

      <!-- Search Overlay -->
      <div class="search-overlay ${this.showSearch ? 'visible' : ''}">
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              class="search-input"
              placeholder="Search neural nodes..."
              .value=${this.searchQuery}
              @input=${this.handleSearchInput}
            />
          </div>
          
          ${this.searchResults.length > 0 ? html`
            <div class="search-results">
              ${this.searchResults.map(node => html`
                <div 
                  class="search-result-item"
                  @click=${() => this.selectSearchResult(node)}
                >
                  <div class="result-icon">${node.icon}</div>
                  <div class="result-content">
                    <div class="result-label">${node.label}</div>
                    <div class="result-description">${node.description}</div>
                  </div>
                  <span class="result-category">${node.category}</span>
                </div>
              `)}
            </div>
          ` : ''}
        </div>
      </div>

      <!-- Tooltip -->
      ${this.tooltipData ? html`
        <div 
          class="tooltip visible"
          style="left: ${this.tooltipPosition.x}px; top: ${this.tooltipPosition.y}px;"
        >
          <span class="tooltip-icon">${this.tooltipData.icon}</span>
          <span class="tooltip-label">${this.tooltipData.label}</span>
          <div class="tooltip-description">${this.tooltipData.description}</div>
        </div>
      ` : ''}

      <!-- Active Node Panel -->
      <div class="active-node-panel ${this.activeNode ? 'visible' : ''}">
        ${this.activeNode ? html`
          <button class="close-panel-btn" @click=${this.closeNodePanel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          
          <div class="panel-header">
            <div class="panel-icon">${this.activeNode.icon}</div>
            <div>
              <div class="panel-title">${this.activeNode.label}</div>
              <div class="panel-category">${this.activeNode.category}</div>
            </div>
          </div>
          
          <p class="panel-description">${this.activeNode.description}</p>
          
          ${this.activeNode.connections?.length > 0 ? html`
            <div class="panel-connections">
              ${this.activeNode.connections.map(connId => {
                const connNode = this.neuralNetwork?.getNodeById(connId)
                return connNode ? html`
                  <span 
                    class="connection-chip"
                    @click=${() => this.selectNode(connNode)}
                  >
                    ${connNode.icon} ${connNode.label}
                  </span>
                ` : ''
              })}
            </div>
          ` : ''}
          
          <button class="navigate-btn" @click=${() => this.navigateToNode(this.activeNode)}>
            Navigate to ${this.activeNode.label} →
          </button>
        ` : ''}
      </div>

      <!-- Minimap with Canvas -->
      <div class="minimap">
        <div class="minimap-header">
          <span class="minimap-label">Neural Map</span>
        </div>
        <canvas class="minimap-canvas"></canvas>
        <span class="minimap-node-count">${nodeCount} nodes</span>
      </div>
    `
  }
}

customElements.define('neural-mesh-navigator', NeuralMeshNavigator)
